// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useHistory, useParams } from "react-router-dom";

// INTERNAL COMPONENTS
import CustomerProfileIndividualFormComponent from "Components/customer-management/customer-profile-individual-form";
import CustomerProfileCompanyFormComponent from "Components/customer-management/customer-profile-company-form";

// HELPERS
import { decode, encode } from "Helpers/enc";
import { toaster } from "Helpers/toaster";

// SERVICES
import { fetchCustomer, updateCustomer } from "Services/customer.service";
import { getCompanyList } from "Services/account.services";

// TYPES
import {
  CustomerCompanyFormInputType,
  CustomerFormInputType,
} from "src/types/customer-form.types";
import { OrgList } from "Types/common.types";
import { useSession } from "Hooks/index";
import ConfirmStatus from "Components/shared/confirm-status/confirm-status";

interface CustomerEditPageProps {}

const CustomerEditPage: React.FC<CustomerEditPageProps> = ({}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { org } = useSession();

  const { id } = useParams<{ id: string }>();
  const customerID = decode(id) || "";

  const [customerDetails, setCustomerDetails] = useState<any>({});
  const [orgList, setOrgList] = useState<OrgList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPrompt, setShowPrompt] = useState<boolean>(false);

  useEffect(() => {
    if (customerID) {
      handleFetchCompanyList();
      handleFetchCustomerDetails();
    }
  }, [customerID]);

  const commonProps = {
    onBack() {
      onBack();
    },
    onSubmitEdit(value) {
      handleUpdateCustomer(value);
    },
    customerDetails,
    orgList,
    isLoading,
  };

  const componentTypeToLoad = () => {
    if (location.pathname.indexOf("/edit/individual") > -1) {
      return <CustomerProfileIndividualFormComponent {...commonProps} />;
    }
    return <CustomerProfileCompanyFormComponent {...commonProps} />;
  };

  const onBack = () => {
    history.goBack();
  };

  const handleFetchCustomerDetails = async () => {
    setIsLoading && setIsLoading(true);
    const responseData = await fetchCustomer(customerID);

    if (responseData && !responseData.error) {
      setCustomerDetails(responseData);
      setIsLoading && setIsLoading(false);
    } else {
      toaster("error", responseData.error.message);
      setIsLoading && setIsLoading(false);
    }
  };

  const handleUpdateCustomer = async (
    data: CustomerFormInputType | CustomerCompanyFormInputType,
  ) => {
    const responseData = await updateCustomer(data);

    if (responseData && !responseData.error) {
      toaster("success", t("toaster.customerInfoUpdated"));
      setShowPrompt && setShowPrompt(true);
    } else {
      if (typeof responseData.error.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", responseData.error.message.message.replace('"', ""));
      }
    }
  };

  const handleFetchCompanyList = async () => {
    const responseData = await getCompanyList(org.logisticProviderOrgID);
    if (responseData && !responseData.error) {
      setOrgList(responseData);
    } else {
      if (typeof responseData.error.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", responseData.error.message.message.replace('"', ""));
      }
    }
  };

  return (
    <div className="customer-management customer-profile-page">
      <Helmet>
        <title>{t("pageTitle.editCustomerProfile")}</title>
      </Helmet>
      <div className="title mb-5">{t("title.customerManagement")}</div>
      <div className="subtitle">{t("title.customerProfile")}</div>
      {showPrompt ? (
        <ConfirmStatus
          title={t("txt.successUpdatedCustomer")}
          subtitle={t("txt.successUpdatedCustomerSubtitle")}
          btnPrm={{
            btnPrmText: t("txt.returnToCustomerList"),
            btnPrmFunc: onBack,
          }}
        />
      ) : (
        componentTypeToLoad()
      )}
    </div>
  );
};

export default CustomerEditPage;
